import React from 'react';
import { usePopper } from 'react-popper';
import styled, { keyframes } from 'styled-components/macro';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslate } from '@federico.moretti/react-translate';
import { SPACINGS, FlexWrapper } from '../../utils/style';
import { Paragraph, Text, ActionText } from '../../typography';
import { IconX, IconMail, IconPhone, IconWrapper } from '../../icons';
import { Button } from '../../atoms';
import Input from '../../atoms/Input';
import TextArea from '../../atoms/TextArea';

const schema = yup.object().shape({
  name: yup.string().required(),
  subject: yup.string().required(),
  message: yup.string().required(),
  email: yup.string().email().required(),
});

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const PopperWrapper = styled.div`
  opacity: 0;
  animation: ${fadeIn} 0.1s ease-in 0.1s forwards;
  z-index: ${(p) => p.theme.zIndexes.overlay};
`;

const PopupWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  margin-right: ${SPACINGS[8]};
  width: 450px;
  padding: ${SPACINGS[8]};
  text-align: start;
  box-shadow: ${(p) => p.theme.boxShadows.base};
  background-color: ${(p) => p.theme.colors.white};
`;

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${(p) => p.theme.colors.primary95w};
  margin: -${SPACINGS[8]};
  margin-top: ${SPACINGS[8]};
  padding: ${SPACINGS[8]};
`;

const FormWrapper = styled.form`
  & > input {
    margin-bottom: ${SPACINGS[4]};
  }
`;

type FormData = {
  subject: string;
  name: string;
  email: string;
  message: string;
  locationUrl: string;
};

type SupportPopupProps = {
  formStatus?: 'loading';
  userName?: string;
  userEmail?: string;
  locationUrl?: string;
  helpEmail: string;
  helpPhone: string;
  handlePopup: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
  onSubmit: (form: FormData) => void;
  children: React.ReactElement<any>;
};

function SupportPopup(props: SupportPopupProps) {
  const {
    userName,
    userEmail,
    helpEmail,
    helpPhone,
    locationUrl,
    formStatus,
    handlePopup,
    onSubmit,
    children,
  } = props;

  const { t } = useTranslate();

  const [isVisible, setIsVisible] = handlePopup;

  const [referenceElement, setReferenceElement] = React.useState(null as any);
  const [popperElement, setPopperElement] = React.useState(null as any);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 20],
        },
      },
      {
        name: 'flip',
        enabled: false,
      },
      {
        name: 'preventOverflow',
        enabled: true,
      },
      {
        name: 'hide',
        enabled: false,
      },
    ],
  });

  const {
    handleSubmit,
    register,
    unregister,
    errors,
    setValue,
  } = useForm<FormData>({
    defaultValues: {
      subject: undefined,
      name: userName,
      email: userEmail,
      message: undefined,
      locationUrl,
    },
    resolver: yupResolver(schema) as any,
  });
  const [isFormVisible, setIsFormVisible] = React.useState(false);
  React.useEffect(() => {
    if (!isVisible) setIsFormVisible(false);
  }, [isVisible]);

  React.useEffect(() => {
    if (userEmail && userName) {
      register('name');
      register('email');

      setValue('name', userName);
      setValue('email', userEmail);
    }

    return () => {
      unregister('name');
      unregister('email');
    };
  }, [userEmail, userName, register, unregister, setValue]);

  React.useEffect(() => {
    if (locationUrl) {
      register('locationUrl');
      setValue('locationUrl', locationUrl);
    }

    return () => {
      unregister('locationUrl');
    };
  }, [locationUrl, register, setValue, unregister]);

  return (
    <>
      {React.cloneElement(children, {
        onClick: () => setIsVisible((b) => !b),
        ref: setReferenceElement,
      })}
      {isVisible && (
        <PopperWrapper
          ref={setPopperElement}
          style={styles.popper}
          {...attributes.popper}
        >
          <PopupWrapper>
            <IconWrapper
              style={{
                cursor: 'pointer',
                position: 'absolute',
                top: SPACINGS[8],
                right: SPACINGS[8],
              }}
              onClick={() => setIsVisible(false)}
            >
              <IconX />
            </IconWrapper>
            <Text size="xl" weight="bold">
              {t('components.supportPopup.title')}
            </Text>
            <Paragraph>{t('components.supportPopup.description')}</Paragraph>
            <Text
              size="sm"
              weight="light"
              opacity={50}
              style={{ marginBottom: SPACINGS[4] }}
            >
              {t('components.supportPopup.officeOpenings')}
            </Text>
            <div>
              <ActionText
                type="link-external"
                to={`mailto:${helpEmail}`}
                style={{ marginRight: SPACINGS[5] }}
              >
                <IconWrapper style={{ marginRight: SPACINGS[1] }}>
                  <IconMail />
                </IconWrapper>
                {helpEmail}
              </ActionText>
            </div>
            {process.env.REACT_APP_ENABLE_SUPPORT_TICKET === 'true' && (
              <ButtonsContainer
                style={isFormVisible ? { marginBottom: SPACINGS[4] } : {}}
              >
                <Paragraph>{t('components.supportPopup.talkToUs')}</Paragraph>
                <Button type="action" onClick={() => setIsFormVisible(true)}>
                  {t('components.supportPopup.fillInForm')}
                </Button>
              </ButtonsContainer>
            )}
            {isFormVisible && (
              <div>
                <Text
                  size="sm"
                  weight="light"
                  opacity={50}
                  style={{ marginBottom: SPACINGS[4] }}
                >
                  {t('components.supportPopup.fillTheFields')}
                </Text>
                <FormWrapper onSubmit={handleSubmit(onSubmit)}>
                  <Text display="block" type="label" htmlFor="subject">
                    {t('subject')}
                  </Text>
                  <Input
                    style={{ width: '100%', marginBottom: SPACINGS[4] }}
                    name="subject"
                    inputRef={register}
                    error={Boolean(errors?.subject)}
                  />
                  {!userName && (
                    <>
                      <Text display="block" type="label" htmlFor="name">
                        {t('name')}
                      </Text>
                      <Input
                        style={{ width: '100%', marginBottom: SPACINGS[4] }}
                        name="name"
                        inputRef={register}
                        error={Boolean(errors?.name)}
                      />
                    </>
                  )}
                  {!userEmail && (
                    <>
                      <Text display="block" type="label" htmlFor="email">
                        {t('email')}
                      </Text>
                      <Input
                        style={{ width: '100%', marginBottom: SPACINGS[4] }}
                        name="email"
                        inputRef={register}
                        error={Boolean(errors?.email)}
                      />
                    </>
                  )}
                  <Text display="block" type="label" htmlFor="message">
                    {t('message')}
                  </Text>
                  <TextArea
                    style={{ width: '100%', height: '6rem' }}
                    name="message"
                    textAreaRef={register}
                    error={Boolean(errors?.message)}
                  />
                  <FlexWrapper
                    style={{ marginTop: SPACINGS[4] }}
                    justify="center"
                  >
                    <Button disabled={formStatus === 'loading'} type="submit">
                      {t('submit')}
                    </Button>
                  </FlexWrapper>
                </FormWrapper>
              </div>
            )}
          </PopupWrapper>
        </PopperWrapper>
      )}
    </>
  );
}

export default SupportPopup;
